<template>
  <div class="profile-page">
    <v-container class="py-10" max-width="800">
      <div class="page-header mb-2">
        <div class="brand-header">
          <router-link to="/">
            <PassingOnLogo class="brand-logo-svg" />
          </router-link>
        </div>
      </div>

      <v-progress-linear v-if="loading" indeterminate color="primary" class="mb-6"></v-progress-linear>

      <v-card v-if="!loading && error" class="main-card error-card mb-6" elevation="0">
        <v-card-text class="text-center">
          <v-icon color="error" size="48" class="mb-3">mdi-alert-circle</v-icon>
          <h2 class="text-h5 mb-2">Unable to load profile</h2>
          <p class="text-body-1 text--secondary">{{ error }}</p>
          <v-btn color="primary" class="mt-4 mr-2" @click="loadProfile">Try Again</v-btn>
          <v-btn color="warning" variant="outlined" class="mt-4" @click="handleLogout">Sign Out</v-btn>
        </v-card-text>
      </v-card>

      <template v-else-if="!loading && user">
        <!-- Account Information -->
        <v-card elevation="0" class="mb-6 main-card">
          <v-card-title class="section-title">
            <v-icon left color="primary">mdi-account</v-icon>
            Account Information
          </v-card-title>
          <v-card-text>
            <v-form ref="profileForm" v-model="formValid">
              <v-text-field
                v-model="firstName"
                label="Name"
                variant="outlined"
                density="comfortable"
                class="mb-4"
              ></v-text-field>

              <v-text-field
                v-model="email"
                label="Email Address"
                variant="outlined"
                density="comfortable"
                readonly
                disabled
                hint="Email cannot be changed"
                persistent-hint
                class="mb-4"
              ></v-text-field>

              <!-- Notification Settings (only show if user has memorials) -->
              <div v-if="!loadingMemorials && ownedMemorials && ownedMemorials.length > 0" class="notification-settings mb-4">
                <h3 class="text-subtitle-1 font-weight-bold mb-3">
                  <v-icon left color="primary" size="small">mdi-bell</v-icon>
                  Notification Preferences
                </h3>
                <v-checkbox
                  v-model="contributionNotifications"
                  label="Notify me when someone adds a contribution to my memory"
                  hint="You'll receive an email notification when someone contributes photos or messages"
                  persistent-hint
                  color="primary"
                  density="comfortable"
                ></v-checkbox>
              </div>

              <v-alert v-if="updateSuccess" type="success" variant="tonal" class="mb-4">
                Profile updated successfully
              </v-alert>

              <v-alert v-if="updateError" type="error" variant="tonal" class="mb-4">
                {{ updateError }}
              </v-alert>

              <v-btn
                color="primary"
                :loading="updating"
                :disabled="!hasChanges || updating"
                @click="updateProfile"
              >
                Save Changes
              </v-btn>
            </v-form>
          </v-card-text>
        </v-card>

        <!-- Your Memorials -->
        <v-card v-if="!loadingMemorials && ownedMemorials && ownedMemorials.length > 0" elevation="0" class="mb-6 main-card">
          <v-card-title class="section-title">
            <v-icon left color="primary">mdi-book-heart</v-icon>
            Your Memories
          </v-card-title>
          <v-card-text>
            <v-list>
              <v-list-item
                v-for="memorial in ownedMemorials"
                :key="memorial.uuid"
                class="memorial-list-item mb-2 clickable-item"
                @click="viewMemorial(memorial.uuid)"
              >
                <template v-slot:prepend>
                  <v-icon color="primary">mdi-book-heart</v-icon>
                </template>
                <v-list-item-title>{{ memorial.nameOfPerson }}</v-list-item-title>
                <v-list-item-subtitle>
                  Created {{ formatDate(memorial.dateCreated) }}
                  <v-chip v-if="!memorial.paymentCompleted" size="x-small" color="warning" class="ml-2">
                    Payment Pending
                  </v-chip>
                </v-list-item-subtitle>
                <template v-slot:append>
                  <v-btn
                    v-if="memorial.uuid"
                    variant="text"
                    color="primary"
                    size="small"
                    @click.stop="viewMemorial(memorial.uuid)"
                  >
                    View
                  </v-btn>
                </template>
              </v-list-item>
            </v-list>
          </v-card-text>
        </v-card>

        <!-- Memories You Administer -->
        <v-card v-if="!loadingMemorials && adminMemorials && adminMemorials.length > 0" elevation="0" class="mb-6 main-card">
          <v-card-title class="section-title">
            <v-icon left color="primary">mdi-shield-account</v-icon>
            Memories You Administer
          </v-card-title>
          <v-card-text>
            <v-list>
              <v-list-item
                v-for="memorial in adminMemorials"
                :key="memorial.uuid"
                class="memorial-list-item mb-2 clickable-item"
                @click="viewMemorial(memorial.uuid)"
              >
                <template v-slot:prepend>
                  <v-icon color="primary">mdi-shield-account</v-icon>
                </template>
                <v-list-item-title>{{ memorial.nameOfPerson }}</v-list-item-title>
                <v-list-item-subtitle>
                  Administrator
                </v-list-item-subtitle>
                <template v-slot:append>
                  <v-btn
                    v-if="memorial.uuid"
                    variant="text"
                    color="primary"
                    size="small"
                    @click.stop="viewMemorial(memorial.uuid)"
                  >
                    View
                  </v-btn>
                </template>
              </v-list-item>
            </v-list>
          </v-card-text>
        </v-card>

        <!-- Your Contributions -->
        <v-card v-if="!loadingMemorials && contributions && contributions.length > 0" elevation="0" class="mb-6 main-card">
          <v-card-title class="section-title">
            <v-icon left color="primary">mdi-heart-multiple</v-icon>
            Your Contributions
          </v-card-title>
          <v-card-text>
            <v-list>
              <v-list-item
                v-for="contribution in contributions"
                :key="contribution.id"
                class="memorial-list-item mb-2 clickable-item"
                @click="viewContribution(contribution.memorial.shareSlug)"
              >
                <template v-slot:prepend>
                  <v-icon color="secondary">mdi-heart</v-icon>
                </template>
                <v-list-item-title>
                  {{ contribution.memorial?.nameOfPerson || 'Unknown Memory' }}
                </v-list-item-title>
                <v-list-item-subtitle>
                  Contributed {{ formatDate(contribution.dateCreated) }}
                  <span v-if="contribution.mediaCount > 0" class="ml-2">
                    · {{ contribution.mediaCount }} {{ contribution.mediaCount === 1 ? 'media item' : 'media items' }}
                  </span>
                </v-list-item-subtitle>
                <template v-slot:append>
                  <v-btn
                    v-if="contribution.memorial?.shareSlug"
                    variant="text"
                    color="secondary"
                    size="small"
                    @click.stop="viewContribution(contribution.memorial.shareSlug)"
                  >
                    View
                  </v-btn>
                </template>
              </v-list-item>
            </v-list>
          </v-card-text>
        </v-card>

        <!-- Actions -->
        <v-card elevation="0" class="main-card">
          <v-card-title class="section-title">
            <v-icon left color="primary">mdi-cog</v-icon>
            Account Actions
          </v-card-title>
          <v-card-text class="pa-6">
            <v-btn
              color="warning"
              variant="outlined"
              prepend-icon="mdi-logout"
              @click="handleLogout"
              class="logout-button"
            >
              Logout
            </v-btn>
          </v-card-text>
        </v-card>
      </template>
    </v-container>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import apiService from '../services/api'
import authService from '../services/auth'
import { useMemorialStore } from '../stores/memorial'

const router = useRouter()
const store = useMemorialStore()

const loading = ref(true)
const loadingMemorials = ref(true)
const updating = ref(false)
const error = ref('')
const updateError = ref('')
const updateSuccess = ref(false)

const user = ref(null)
const ownedMemorials = ref([])
const adminMemorials = ref([])
const contributions = ref([])

const firstName = ref('')
const email = ref('')
const contributionNotifications = ref(true)

const originalFirstName = ref('')
const originalContributionNotifications = ref(true)

const formValid = ref(false)

const hasChanges = computed(() => {
  return firstName.value !== originalFirstName.value ||
         contributionNotifications.value !== originalContributionNotifications.value
})

const loadProfile = async () => {
  loading.value = true
  error.value = ''

  try {
    const response = await apiService.get('/user')
    if (response.success && response.user) {
      user.value = response.user
      firstName.value = response.user.firstName || ''
      email.value = response.user.email || ''
      contributionNotifications.value = response.user.contributionNotifications !== undefined
        ? response.user.contributionNotifications
        : true

      originalFirstName.value = firstName.value
      originalContributionNotifications.value = contributionNotifications.value
    } else {
      error.value = response.msg || 'Unable to load profile'
    }
  } catch (err) {
    console.error('Profile load error:', err)
    error.value = 'Unable to load your profile. Please try again.'
  } finally {
    loading.value = false
  }
}

const loadMemorials = async () => {
  loadingMemorials.value = true

  try {
    const response = await apiService.listMemorialsAndContributions()
    if (response.success) {
      ownedMemorials.value = response.ownedMemorials || []
      adminMemorials.value = response.adminMemorials || []
      contributions.value = response.contributions || []
    }
  } catch (err) {
    console.error('Memorials and contributions load error:', err)
  } finally {
    loadingMemorials.value = false
  }
}

const updateProfile = async () => {
  updateError.value = ''
  updateSuccess.value = false
  updating.value = true

  try {
    const response = await apiService.post('/user/save', {
      email: email.value, // Include email even though it's read-only
      firstName: firstName.value,
      surname: null, // Set surname to null since we're not using it
      contributionNotifications: contributionNotifications.value
    })

    if (response.success) {
      updateSuccess.value = true
      originalFirstName.value = firstName.value
      originalContributionNotifications.value = contributionNotifications.value

      setTimeout(() => {
        updateSuccess.value = false
      }, 3000)
    } else {
      updateError.value = response.msg || 'Unable to update profile'
    }
  } catch (err) {
    console.error('Profile update error:', err)
    updateError.value = 'Unable to update profile. Please try again.'
  } finally {
    updating.value = false
  }
}

const formatDate = (timestamp) => {
  if (!timestamp) return ''
  const date = new Date(timestamp)
  return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
}

const viewMemorial = (memorialUuid) => {
  // Navigate to the memorial loading page with the UUID
  router.push(`/memory/load/${memorialUuid}`)
}

const viewContribution = (shareSlug) => {
  // Navigate to the for page to view/edit contribution
  // Add sli=true parameter to keep user logged in
  router.push(`/for/${shareSlug}?sli=true`)
}

const handleLogout = () => {
  authService.logout()
}

onMounted(async () => {
  // Check if user is authenticated (use store as single source of truth)
  if (!store.apiToken) {
    router.push('/login')
    return
  }

  await loadProfile()
  await loadMemorials()
})
</script>

<style scoped>
.profile-page {
  min-height: 100vh;
  background: linear-gradient(180deg, var(--v-theme-background) 0%, var(--v-theme-surface) 100%);
}

.main-card {
  border-radius: 20px;
  border: 1px solid rgba(148, 163, 184, 0.25);
  box-shadow: 0 24px 45px -20px rgba(15, 23, 42, 0.2);
  background: var(--v-theme-surface);
}

.error-card {
  border: 1px solid rgba(239, 68, 68, 0.25);
}

.brand-header {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
}

.brand-logo {
  height: 40px;
  width: auto;
}

.brand-title {
  letter-spacing: 0.1rem;
  font-weight: 600;
  color: rgb(var(--v-theme-primary));
  margin: 0;
}

.page-title {
  font-weight: 600;
  color: var(--v-theme-black);
  text-align: center;
}

.section-title {
  font-weight: 600 !important;
  background: var(--v-theme-background);
  border-bottom: 1px solid var(--v-theme-border);
}

.memorial-list-item {
  border: 1px solid var(--v-theme-border);
  border-radius: 12px;
  padding: 8px;
}

.memorial-list-item:hover {
  background: var(--v-theme-background);
}

.clickable-item {
  cursor: pointer;
}

.logout-button {
  min-width: 120px;
  width: auto;
}
</style>
